import dates from 'src/utils/dates'
import format from 'src/utils/format'
import gam from '@/utils/gam'

const org = {
  data: {
    oao: { reportId: 0, companyName: 'OAO' },
    misconfigured: { publisherId: null, billingCode: 'Misconfigured' },
    teams: {
      all: { teamCode: '-1', teamId: '-1', teamName: 'All Teams' },
      unknown: { teamCode: '-2', teamId: '-2', teamName: 'Team Unknown' },
    },
    unknown: { publisherId: null, billingCode: 'Unknown' },
  },
  defaults: {
    billingCode: null,
    clientName: null,
    isAdministrative: false,
  },

  isValidOrgProperty(propertyName, rawValue) {
    const isDefined = (value) => value !== undefined
    const isBoolean = (value) => typeof value === 'boolean'

    const validationRules = {
      hasCustomReports: isDefined,
      hasDiscrepancy: isBoolean,
      hasPacing: isDefined,
      hasHealthAdx: isBoolean,
      hasHealthNetwork: isBoolean,
      hasProgrammatic: isBoolean,
      hasYieldPartner: isBoolean,
      isTimesheetEnabled: isDefined,
      isAdministrative: isDefined,
    }

    return validationRules[propertyName](rawValue)
  },
  formatOrgProperties(propertyName, rawValue) {
    const isValidValue = this.isValidOrgProperty(propertyName, rawValue)

    return isValidValue ? format.titlecase(rawValue) : 'N/A'
  },
  async mapClientAndPublisher(client = {}, publisher = {}, config = {}) {
    const settings = config.settings || { filters: [], teams: [] }
    const accessConfig = config.access || {
      active: true,
      permissions: { pacing: true, discrepancy: true, customReports: [] },
    }
    const { expires, permissions = {} } = accessConfig

    const defaultOrgName = 'Name Unknown'
    // Performance requires avoiding destructuring on these large objects.
    /* eslint-disable-next-line prefer-destructuring */
    const clientId = client.clientId
    /* eslint-disable-next-line prefer-destructuring */
    const publisherId = publisher.publisherId
    /* eslint-disable-next-line prefer-destructuring */
    const hasGamTeams = publisher.hasTeam || false
    /* eslint-disable-next-line prefer-destructuring */
    const networkCode = publisher.networkCode

    const billingCode = client.billingCode || publisher.billingCode

    const filters = (Array.isArray(settings.filters) && settings.filters) || []

    const hasProductTeamPermissions = permissions.teams || false
    const teamConfigs = (Array.isArray(settings.teams) && settings.teams) || []
    // Filter and map team configs to create an array of active product teams.
    const productTeams = !hasProductTeamPermissions ? [] : teamConfigs.reduce((activeProductTeams, team) => {
      if (team.isActive) {
        // Set `billingCode` to emulate an API `team`.
        // Set `publisherId` to emulate an API `team`.
        // Set `networkCode` to empower search by networkCode.
        // Finally, set the properties configured on the product team,
        // overriding the properties inherited from the org.
        activeProductTeams.push({
          billingCode,
          publisherId,
          networkCode,
          ...team,
        })
      }

      return activeProductTeams
    }, [])

    const gamTeamsPromise = publisher.teams

    const orgName = client.clientName || publisher.name || defaultOrgName
    const foundIdMismatch = clientId && publisherId && clientId !== publisherId

    const hasPacing = !!publisher.billingCode
    const isTimesheetEnabled = !!client.billingCode && !client.isDeleted

    const hasAccessExpired = expires && dates.areDatesGte(expires, dates.now())
    const isOrgActive = client.isActive && publisher.isActive && accessConfig.active !== false && !hasAccessExpired
    const hasInternalConfig = config.isInternal

    const hasPacingConfig = hasPacing && (hasInternalConfig || [true, undefined].indexOf(permissions?.pacing) !== -1)
    const hasDiscrepancyConfig = publisher.hasDiscrepancy && (hasInternalConfig || [true, undefined].indexOf(permissions?.discrepancy) !== -1)
    const hasCustomReports = Array.isArray(permissions?.customReports) && permissions.customReports.length > 0 && permissions.customReports.filter((perm) => perm.active).length > 0
    const hasHealthAdx = publisher.hasHealthAdx || publisher.hasHealthAdx

    const hasCustomReportsView = this.formatOrgProperties('hasCustomReports', hasCustomReports)
    const hasPacingReport = this.formatOrgProperties('hasPacing', hasPacingConfig)
    const hasTimesheetReport = this.formatOrgProperties('isTimesheetEnabled', isTimesheetEnabled)
    const hasDiscrepancyReport = this.formatOrgProperties('hasDiscrepancy', hasDiscrepancyConfig)
    const hasHealthAdxReport = this.formatOrgProperties('hasHealthAdx', hasHealthAdx)
    const hasHealthNetworkReport = this.formatOrgProperties('hasHealthNetwork', publisher.hasHealthNetwork)
    const hasYieldPartnerReport = this.formatOrgProperties('hasYieldPartner', publisher.hasYieldPartner)
    const isInternalUseOnly = this.formatOrgProperties('isAdministrative', client.isAdministrative)

    const defaultTeam = {
      ...this.data.teams.all, // Set default `teamCode` and `teamId` properties.
      teamName: `${orgName}: All Teams`, // Set `teamName` to emulate an API `team`.
      billingCode, // Set `billingCode` to emulate an API `team`.
      publisherId, // Set `publisherId` to emulate an API `team`.
    }

    // The API response supersedes the Firestore configuration: config < publisher < client.
    // Client supersedes publisher, and they both supersede the organization config.
    const item = { ...config, ...publisher, ...client }
    const validNetworkCode = networkCode && Number(networkCode) >= 0

    const gamTeamsResponse = hasGamTeams ? await gamTeamsPromise : []
    const { content: gamTeamsContent = gamTeamsResponse, error: gamTeamsError } = gamTeamsResponse
    const gamTeams = !gamTeamsError && Array.isArray(gamTeamsContent) ? gamTeamsContent : []
    const assignedTeams = hasProductTeamPermissions ? [defaultTeam].concat(productTeams, gamTeams) : [defaultTeam].concat(gamTeams)

    /** @section Org Identifiers */
    item.billingCode = billingCode
    item.orgName = orgName
    item.reportIds = [billingCode, networkCode, publisherId]

    /** @section Org Links */
    item.href = `/internal/organization/${billingCode}`
    item.adServerUrl = validNetworkCode ? gam.network(networkCode) : null

    /** @section Org Errors */
    item.foundIdMismatch = foundIdMismatch

    /** @section Org Grants */
    item.isOrgActive = isOrgActive
    item.hasAccessExpired = hasAccessExpired
    item.hasCustomReports = hasCustomReports
    // @TODO Rename hasCustomReportsView to s/t that represents it's for printing.
    item.hasCustomReportsView = hasCustomReportsView
    item.hasDiscrepancy = hasDiscrepancyConfig
    item.hasDiscrepancyReport = hasDiscrepancyReport
    item.hasPacing = hasPacingConfig
    item.hasPacingReport = hasPacingReport
    item.hasHealthAdxReport = hasHealthAdxReport
    item.hasHealthNetworkReport = hasHealthNetworkReport
    // item.hasProgrammatic = hasProgrammatic // inherited
    // item.hasYieldPartner = hasYieldPartner // inherited
    item.hasYieldPartnerReport = hasYieldPartnerReport
    item.hasTimesheetReport = hasTimesheetReport

    item.isInternalUseOnly = isInternalUseOnly
    item.isTimesheetEnabled = isTimesheetEnabled

    /** @section Org Teams */
    item.hasGamTeams = hasGamTeams && Array.isArray(gamTeams)
    item.hasProductTeams = hasProductTeamPermissions && Array.isArray(productTeams)
    item.hasAssignedTeams = assignedTeams.length > 0

    item.gamTeams = gamTeams
    item.productTeams = productTeams
    item.assignedTeams = assignedTeams

    /** @section Org as an Option */
    item.operator = 'incl'
    item.text = orgName
    item.value = billingCode

    // Set the access property to normalize the structure of the access object.
    item.access = { ...accessConfig }

    // Set the settings property to normalize the structure of the settings object.
    item.settings = { ...settings, filters, teams: teamConfigs }

    // The teams are defined near last to lend each team all the properties calculated on the org.
    item.teams = assignedTeams.map(this.mapTeamOption.bind(this, item))

    // The dataType property is used to identify the type of item in the search results.
    item.dataType = 'organization'

    return item
  },
  async reduceClientsAndPublishers(orgConfigs, orgsArrayPromise, clientOrPublisher, index, clientsAndPublishers) {
    const orgsArray = await orgsArrayPromise
    const { billingCode } = clientOrPublisher
    const inArray = orgsArray.find((o) => o.billingCode === billingCode)

    if (inArray) return orgsArray

    if (!Array.isArray(clientsAndPublishers)) {
      throw new Error('Organization Error: Clients and Publishers Not Found')
    }

    const matches = clientsAndPublishers.filter((p) => p.billingCode === billingCode)

    if (!matches?.length) return orgsArray

    const foundOrgConfig = orgConfigs.find((orgConfig) => orgConfig?.docId === billingCode)
    const makeDefaultConfig = () => ({ access: {}, isInternal: (orgConfigs[0] || {}).isInternal, settings: {} })
    const config = foundOrgConfig || makeDefaultConfig()

    const client = matches.find((item) => item.isObjectClient) || {}
    const publisher = matches.find((item) => item.isObjectPublisher) || {}

    const result = await this.mapClientAndPublisher(client, publisher, config)

    orgsArray.push(result)

    return orgsArray
  },

  getTeamName(networkTeamId, publisher, team = {}) {
    const { isProcessed, teamName = `Team ${networkTeamId}` } = team

    // Exit if this process has been executed on this object.
    // This block handles cases where org data is shared across processes.
    // For example, both auth and search leverage organization data,
    // so search may use the organization data that auth has already
    // transformed for its own purposes.
    if (isProcessed) return teamName

    const invalidTeamId = !networkTeamId && networkTeamId !== 0
    const { name: publisherName, clientName } = publisher

    // Handle clients that are not publishers.
    if (!publisherName && clientName) return `${clientName}: All Access`

    // Handle misconfigurations and teams that no longer exist.
    if (invalidTeamId) return `${publisherName}: Team Unknown`

    // Per this block, an "all access" team is a combination of teamId and teamCode equal to '-1'.
    if (team.teamId === '-1' && team.teamCode === '-1') {
      return `${publisherName}: All Teams`
    }

    // Concat the team name to the publisher name, using a colon as a delimiter.
    return `${publisherName}: ${teamName}`
  },
  mapTeamOption(publisher, publisherTeam) {
    const wasAlreadyProcessed = publisher.isProcessed && publisherTeam.isProcessed

    if (wasAlreadyProcessed) return publisherTeam

    const team = { ...publisher, ...publisherTeam }
    const { billingCode, hasProductTeams, teamCode = team.code, teamId = team.id } = team

    const teamIdString = String(teamId)
    const teamCodeString = String(teamCode)

    const teamName = this.getTeamName(teamId, publisher, team)
    const isAllAccess = ['-1', '*'].indexOf(teamCodeString) > -1

    // Identify iAdOps teams, aka product teams.
    // Product teams are not to be confused with GAM teams.
    team.isProductTeam = hasProductTeams && teamCode !== '-1'

    // Set properties for Fuse.
    team.code = teamCodeString
    team.id = teamIdString

    // Set team identifiers.
    team.teamCode = teamCodeString
    team.teamId = teamIdString
    team.teamName = teamName

    // Set properties for routes.
    team.subId = team.isProductTeam ? teamCodeString : teamIdString
    // @clean
    // team.reportId = isAllAccess ? `${billingCode}` : `${billingCode}/${team.subId}`
    team.reportId = billingCode
    team.uniqueId = `${billingCode}/${team.subId}`

    // Set team links.
    team.href = `/internal/organization/${billingCode}/${team.subId}`

    // Set team grants.
    team.isAllAccess = isAllAccess

    team.hasHealthAdx = publisher.hasHealthAdx
    team.hasHealthNetwork = publisher.hasHealthNetwork
    team.hasYieldPartner = publisher.hasYieldPartner

    // Set properties of a search option.
    team.operator = 'incl'
    team.text = teamName
    team.value = teamIdString

    // Set data processing properties.
    team.dataType = 'team'
    team.isProcessed = true

    return team
  },

  flattenNetworkData(networks) {
    const networkSeed = {
      billingCodes: [],
      orgNames: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }
    return networks.reduce((networkData, nw) => {
      const { publisher } = nw
      const { billingCode, publisherId, orgName, teams } = publisher

      networkData.billingCodes.push(billingCode)
      networkData.orgNames.push(orgName)

      teams.forEach((team) => {
        const { teamCode, teamId } = team

        networkData.teamCodes.push(`${billingCode}/${teamCode}`)
        networkData.teamIds.push(`${publisherId}/${teamId}`)
        networkData.teamNames.push(this.getTeamName(teamId, publisher, team))
      })

      return networkData
    }, networkSeed)
  },
}

export default org
